import {
  ALLOWED_ATTR_SET,
  ALLOWED_TAG_SET,
  FORBID_TAG_SET,
} from './htmlPolicy';

const BLOCK_TAGS = new Set([
  'blockquote',
  'div',
  'h1',
  'h2',
  'h3',
  'ol',
  'p',
  'pre',
  'table',
  'ul',
]);

const URL_ATTRS = new Set(['href', 'src']);
const COMMON_ALLOWED_ATTRS = new Set(['colspan', 'height', 'rowspan', 'width']);
const TAG_ALLOWED_ATTRS: Record<string, Set<string>> = {
  a: new Set(['href', 'rel', 'target', 'title']),
  img: new Set(['alt', 'height', 'src', 'title', 'width']),
  td: COMMON_ALLOWED_ATTRS,
  th: COMMON_ALLOWED_ATTRS,
};

const MAX_HTML_LENGTH = 200_000;
const MAX_TABLE_ROWS_PER_TABLE = 500;
const MAX_NODE_COUNT = 8_000;

export function cleanOfficeHtml(html: string): string {
  if (!html) {
    return '';
  }

  const boundedHtml = html.length > MAX_HTML_LENGTH ? html.slice(0, MAX_HTML_LENGTH) : html;

  let cleaned = stripZeroWidthChars(boundedHtml);
  cleaned = detectAndCleanWord(cleaned);
  cleaned = detectAndCleanGoogleDocs(cleaned);
  cleaned = cleaned.replace(/<\?(?:xml|mso)[^>]*>/gi, '');

  const parser = new DOMParser();
  const doc = parser.parseFromString(`<div>${cleaned}</div>`, 'text/html');
  const root = doc.body.firstElementChild as HTMLElement | null;

  if (!root) {
    return '';
  }

  removeDangerousNodes(root);
  normalizeInlineFormatting(root);
  normalizeBlocks(root);
  normalizeLists(root, doc);
  normalizeTables(root, doc);
  normalizeImageDimensions(root);
  normalizeCodeBlocks(root, doc);
  normalizeEmailBlocks(root);
  normalizePdfArtifacts(root);
  enforceNodeCountLimit(root);
  sanitizeDomByPolicy(root, doc);
  stripEmptyBlocks(root);

  return root.innerHTML;
}

export function sanitizeHtmlForSubmission(html: string): string {
  return cleanOfficeHtml(html);
}

function stripZeroWidthChars(html: string): string {
  return html.replace(/[\u200B\u200C\u200D\uFEFF]/g, '');
}

function detectAndCleanWord(html: string): string {
  const lower = html.toLowerCase();
  const isWord = lower.includes('class="mso')
    || lower.includes('mso-')
    || lower.includes('xmlns:w="urn:schemas-microsoft-com:office:word"');

  if (!isWord) {
    return html;
  }

  return html
    .replace(/<o:p>\s*<\/o:p>/gi, '')
    .replace(/<o:p>.*?<\/o:p>/gis, ' ')
    .replace(/<!--\[if.*?endif\]-->/gis, '')
    .replace(/\sclass="Mso[^"]*"/gi, '')
    .replace(/\smso-[a-z-]+:[^;"']+;?/gi, '')
    .replace(/<w:[^>]+>.*?<\/w:[^>]+>/gis, '');
}

function detectAndCleanGoogleDocs(html: string): string {
  const lower = html.toLowerCase();
  const isGoogleDocs = lower.includes('docs-internal-guid')
    || lower.includes('data-google')
    || lower.includes('id="docs-internal-guid');

  if (!isGoogleDocs) {
    return html;
  }

  return html
    .replace(/id="docs-internal-guid-[^"]*"/gi, '')
    .replace(/\sdata-[^=]+="[^"]*"/gi, '');
}

function removeDangerousNodes(root: HTMLElement) {
  const nodes = root.querySelectorAll('*');
  for (const node of nodes) {
    const tag = node.tagName.toLowerCase();
    if (FORBID_TAG_SET.has(tag)) {
      node.remove();
      continue;
    }

    const style = (node.getAttribute('style') || '').toLowerCase();
    if (style.includes('display:none') || style.includes('visibility:hidden')) {
      node.remove();
      continue;
    }

    if (tag === 'img') {
      const width = Number(node.getAttribute('width') || 0);
      const height = Number(node.getAttribute('height') || 0);
      const src = (node.getAttribute('src') || '').trim().toLowerCase();
      const looksLikeTrackingPixel = width === 1 && height === 1 && src.startsWith('http');
      if (looksLikeTrackingPixel) {
        node.remove();
      }
    }
  }
}

function normalizeInlineFormatting(root: HTMLElement) {
  const elements = Array.from(root.querySelectorAll('*'));
  for (const element of elements) {
    const tag = element.tagName.toLowerCase();
    const style = (element.getAttribute('style') || '').toLowerCase();

    if (tag === 'b') {
      replaceTag(element, 'strong');
      continue;
    }
    if (tag === 'i') {
      replaceTag(element, 'em');
      continue;
    }

    if (tag === 'span') {
      const shouldBold = /font-weight\s*:\s*(bold|700|800|900)/i.test(style);
      const shouldItalic = /font-style\s*:\s*italic/i.test(style);
      const shouldUnderline = /text-decoration[^;]*:\s*underline/i.test(style);
      unwrapStyledSpan(element, shouldBold, shouldItalic, shouldUnderline);
    }
  }
}

function unwrapStyledSpan(element: Element, bold: boolean, italic: boolean, underline: boolean) {
  const doc = element.ownerDocument;
  if (!doc || !element.parentNode) {
    return;
  }

  let replacement: Node = doc.createDocumentFragment();
  for (const child of Array.from(element.childNodes)) {
    replacement.appendChild(child.cloneNode(true));
  }

  if (underline) {
    replacement = wrapNode(doc, replacement, 'u');
  }
  if (italic) {
    replacement = wrapNode(doc, replacement, 'em');
  }
  if (bold) {
    replacement = wrapNode(doc, replacement, 'strong');
  }

  element.replaceWith(replacement);
}

function wrapNode(doc: Document, content: Node, tagName: string): Node {
  const wrapper = doc.createElement(tagName);
  wrapper.appendChild(content);
  return wrapper;
}

function normalizeBlocks(root: HTMLElement) {
  const divs = Array.from(root.querySelectorAll('div'));
  for (const div of divs) {
    const hasBlockChildren = Array.from(div.children).some((child) => BLOCK_TAGS.has(child.tagName.toLowerCase()));
    if (hasBlockChildren) {
      unwrapElement(div);
      continue;
    }

    const p = div.ownerDocument.createElement('p');
    p.innerHTML = div.innerHTML;
    div.replaceWith(p);
  }
}

function normalizeLists(root: HTMLElement, doc: Document) {
  const paragraphs = Array.from(root.querySelectorAll('p'));
  const converted: Element[] = [];

  for (const paragraph of paragraphs) {
    const text = (paragraph.textContent || '').replace(/\u00A0/g, ' ').trim();
    if (!text) {
      continue;
    }

    const bulletMatch = text.match(/^(?:[•·\-])\s+(.+)$/);
    if (bulletMatch) {
      const li = doc.createElement('li');
      li.innerHTML = stripListPrefix(paragraph.innerHTML, /^(?:&nbsp;|\s)*(?:[•·\-])\s+/i);
      li.setAttribute('data-list-kind', 'ul');
      paragraph.replaceWith(li);
      converted.push(li);
      continue;
    }

    const numberedMatch = text.match(/^\d+[\.\)\-]\s+(.+)$/);
    if (numberedMatch) {
      const li = doc.createElement('li');
      li.innerHTML = stripListPrefix(paragraph.innerHTML, /^(?:&nbsp;|\s)*\d+[\.\)\-]\s+/i);
      li.setAttribute('data-list-kind', 'ol');
      paragraph.replaceWith(li);
      converted.push(li);
    }
  }

  for (const li of converted) {
    if (!li.parentElement || li.parentElement.tagName.toLowerCase() !== 'div') {
      // continue grouping regardless of parent type
    }

    const kind = li.getAttribute('data-list-kind');
    if (!kind) {
      continue;
    }
    const listTag = kind === 'ol' ? 'ol' : 'ul';
    const previous = li.previousElementSibling;
    if (previous && previous.tagName.toLowerCase() === listTag) {
      li.removeAttribute('data-list-kind');
      previous.appendChild(li);
      continue;
    }

    const list = doc.createElement(listTag);
    li.removeAttribute('data-list-kind');
    li.replaceWith(list);
    list.appendChild(li);

    let next = list.nextElementSibling;
    while (next && next.tagName.toLowerCase() === 'li' && next.getAttribute('data-list-kind') === kind) {
      const current = next;
      next = next.nextElementSibling;
      current.removeAttribute('data-list-kind');
      list.appendChild(current);
    }
  }
}

function stripListPrefix(inputHtml: string, pattern: RegExp): string {
  return inputHtml.replace(pattern, '');
}

function normalizeTables(root: HTMLElement, doc: Document) {
  const tables = Array.from(root.querySelectorAll('table'));
  for (const table of tables) {
    for (const attr of Array.from(table.attributes)) {
      const name = attr.name.toLowerCase();
      if (name === 'width' || name === 'height' || name === 'border' || name === 'cellpadding' || name === 'cellspacing' || name === 'style' || name === 'class' || name === 'id') {
        table.removeAttribute(attr.name);
      }
    }

    if (!table.querySelector('tbody')) {
      const tbody = doc.createElement('tbody');
      const movable = Array.from(table.children).filter((child) => child.tagName.toLowerCase() === 'tr');
      for (const row of movable) {
        tbody.appendChild(row);
      }
      table.appendChild(tbody);
    }

    const rows = Array.from(table.querySelectorAll('tr'));
    if (rows.length > MAX_TABLE_ROWS_PER_TABLE) {
      rows.slice(MAX_TABLE_ROWS_PER_TABLE).forEach((row) => row.remove());
    }

    for (const row of rows) {
      const hasTh = row.querySelector('th') !== null;
      const hasTd = row.querySelector('td') !== null;
      if (hasTh && hasTd) {
        for (const th of Array.from(row.querySelectorAll('th'))) {
          replaceTag(th, 'td');
        }
      }
    }
  }
}

function normalizeImageDimensions(root: HTMLElement) {
  const images = Array.from(root.querySelectorAll('img'));
  for (const image of images) {
    const style = image.getAttribute('style') || '';
    const styleWidth = extractPixelDimension(style, 'width');
    const styleHeight = extractPixelDimension(style, 'height');

    const attrWidth = parsePositiveInt(image.getAttribute('width'));
    const attrHeight = parsePositiveInt(image.getAttribute('height'));

    const finalWidth = clampImageDimension(styleWidth ?? attrWidth ?? undefined);
    const finalHeight = clampImageDimension(styleHeight ?? attrHeight ?? undefined);

    if (finalWidth) {
      image.setAttribute('width', finalWidth.toString());
    }
    if (finalHeight) {
      image.setAttribute('height', finalHeight.toString());
    }
  }
}

function normalizeCodeBlocks(root: HTMLElement, doc: Document) {
  const blockCandidates = Array.from(root.querySelectorAll('p,div,pre'));
  for (const candidate of blockCandidates) {
    const text = candidate.textContent || '';
    const match = text.match(/^\s*```[a-z0-9_-]*\n([\s\S]*?)\n```\s*$/i);
    if (!match) {
      continue;
    }

    const pre = doc.createElement('pre');
    const code = doc.createElement('code');
    code.textContent = match[1];
    pre.appendChild(code);
    candidate.replaceWith(pre);
  }
}

function normalizeEmailBlocks(root: HTMLElement) {
  const blocks = Array.from(root.querySelectorAll('blockquote'));
  for (const block of blocks) {
    for (const child of Array.from(block.children)) {
      if (child.tagName.toLowerCase() === 'div') {
        replaceTag(child, 'p');
      }
    }
  }
}

function normalizePdfArtifacts(root: HTMLElement) {
  const textNodes: Text[] = [];
  const walker = root.ownerDocument.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    textNodes.push(walker.currentNode as Text);
  }

  for (const node of textNodes) {
    const normalized = node.textContent
      ?.replace(/([A-Za-z])-\s*\n\s*([a-z])/g, '$1$2')
      .replace(/\s+\n\s+/g, ' ')
      .replace(/\u00A0/g, ' ');
    if (normalized !== undefined) {
      node.textContent = normalized;
    }
  }
}

function sanitizeDomByPolicy(root: HTMLElement, doc: Document) {
  const elements = Array.from(root.querySelectorAll('*'));

  for (const element of elements) {
    const tag = element.tagName.toLowerCase();
    if (!ALLOWED_TAG_SET.has(tag)) {
      unwrapElement(element);
      continue;
    }

    for (const attr of Array.from(element.attributes)) {
      const name = attr.name.toLowerCase();
      const value = attr.value.trim();

      if (name.startsWith('on') || name === 'style' || name === 'class' || name === 'id' || name.startsWith('data-')) {
        element.removeAttribute(attr.name);
        continue;
      }

      const allowedForTag = TAG_ALLOWED_ATTRS[tag];
      const allowedByCommon = COMMON_ALLOWED_ATTRS.has(name);
      const allowedByTag = allowedForTag?.has(name) ?? false;
      if (!ALLOWED_ATTR_SET.has(name) || (!allowedByTag && !allowedByCommon)) {
        element.removeAttribute(attr.name);
        continue;
      }

      if (URL_ATTRS.has(name)) {
        const normalized = sanitizeUrl(value);
        if (!normalized) {
          element.removeAttribute(attr.name);
          continue;
        }
        if (name === 'href') {
          element.setAttribute(name, stripUtmParams(normalized, doc));
          if (element.getAttribute('target') === '_blank') {
            element.setAttribute('rel', 'noopener noreferrer');
          }
        } else {
          element.setAttribute(name, normalized);
        }
      }
    }
  }
}

function sanitizeUrl(url: string): string | null {
  const value = url.trim();
  if (!value) {
    return null;
  }

  const lower = value.toLowerCase();
  if (
    lower.startsWith('javascript:')
    || lower.startsWith('vbscript:')
    || lower.startsWith('data:text/html')
    || lower.startsWith('file:')
  ) {
    return null;
  }

  if (lower.startsWith('data:')) {
    return lower.startsWith('data:image/') ? value : null;
  }

  return value;
}

function enforceNodeCountLimit(root: HTMLElement) {
  const nodes = root.querySelectorAll('*');
  if (nodes.length <= MAX_NODE_COUNT) {
    return;
  }

  const overflow = Array.from(nodes).slice(MAX_NODE_COUNT);
  for (const node of overflow) {
    node.remove();
  }
}

function extractPixelDimension(style: string, property: 'width' | 'height'): number | null {
  const match = style.match(new RegExp(`${property}\\s*:\\s*([0-9]+(?:\\.[0-9]+)?)px`, 'i'));
  if (!match) {
    return null;
  }

  const parsed = Number.parseFloat(match[1]);
  return Number.isFinite(parsed) && parsed > 0 ? Math.round(parsed) : null;
}

function parsePositiveInt(value: string | null): number | null {
  if (!value) {
    return null;
  }

  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
}

function clampImageDimension(value?: number): number | null {
  if (!value || value <= 0) {
    return null;
  }

  return Math.min(Math.max(Math.round(value), 1), 2400);
}

function stripUtmParams(url: string, doc: Document): string {
  try {
    const parsed = new URL(url, doc.baseURI || window.location.origin);
    for (const key of Array.from(parsed.searchParams.keys())) {
      if (key.toLowerCase().startsWith('utm_')) {
        parsed.searchParams.delete(key);
      }
    }
    return parsed.toString();
  } catch {
    return url;
  }
}

function stripEmptyBlocks(root: HTMLElement) {
  const blocks = Array.from(root.querySelectorAll('p,div,span'));
  for (const block of blocks) {
    const text = (block.textContent || '').replace(/\u00A0/g, ' ').trim();
    const hasMedia = block.querySelector('img,table,ul,ol,pre,blockquote') !== null;
    if (!text && !hasMedia) {
      block.remove();
    }
  }
}

function unwrapElement(element: Element) {
  const parent = element.parentNode;
  if (!parent) {
    return;
  }
  while (element.firstChild) {
    parent.insertBefore(element.firstChild, element);
  }
  parent.removeChild(element);
}

function replaceTag(element: Element, tagName: string) {
  const doc = element.ownerDocument;
  const replacement = doc.createElement(tagName);
  for (const attr of Array.from(element.attributes)) {
    replacement.setAttribute(attr.name, attr.value);
  }
  replacement.innerHTML = element.innerHTML;
  element.replaceWith(replacement);
}
